import { supabase } from "../../config/supabaseClient";
import { carouselSupportsDescription, isValidCarouselType, trimCarouselDescription } from "../helpers/carouselHelper";
import {
  buildArchiveStatusPatch,
  getArchiveFieldsForInsert,
  getNextArchiveIndex,
  getArchiveTableName,
  isMissingArchiveColumnError,
} from "../StayUpToDate/archiveMetadata";
import {
  createComponentSlug,
  getComponentStatus,
  getComponentsTableName,
  isCreatorPagesMode,
} from "../componentsTable";
import { getActiveCreatorPageId } from "../../features/creator-pages/storage";
import {
  deleteLinkedCreatorPageForSourceComponent,
  syncCreatorPageMetadataFromSourceComponent,
  syncLinkedCreatorComponentSettingsFromPage,
  syncLinkedCreatorComponentSettingsFromSource,
  syncSourceComponentMetadataFromPageComponent,
} from "../../features/creator-pages/api";
import { notifyCampaignsUpdated } from "./CampaignDatabase";
import { softDeleteUserLinksForLinksSection } from "./UserLinksDatabase";
import type { PersistedImageCrop } from "../imageCrop/cropStatePersist";
import { isMissingImageCropColumnError } from "../imageCrop/cropStatePersist";
import { markLinkInBioContentEdited } from "../../Screens/DashboardView/GettingStarted/gettingStartedProgress";
import {
  carouselAnalyticsLookupKeys,
  parseAnalyticsDetails,
} from "../analytics/carouselAnalyticsKey";
import {
  applyWebsiteLibLocalPatch,
  hardDeleteWebsiteLibSessionCreatedRow,
  isLibPendingPublishComponent,
  isWebsiteLibPersistGated,
  isWebsiteLibSessionCreated,
  LIB_PENDING_PUBLISH_STATUS,
  markWebsiteLibDirty,
  noteWebsiteLibCreatedRow,
  noteWebsiteLibDeletedId,
  shouldDeferWebsiteLibWrite,
} from "../../Screens/DashboardView/MasterTemplateEditor/websiteLibPersistGate";
import { noteWebsiteTemplateDeletedId } from "../../Screens/DashboardView/MasterTemplateEditor/websiteTemplateComponentSession";
import { TEMPLATE_COMPONENTS_TABLE } from "../StayUpToDate/componentSurface";
import {
  buildComponentCopyName,
  buildDuplicatedComponentPayload,
  buildUniqueNameCandidates,
  isCustomShoppableCarousel,
  UNIQUE_COMPONENT_NAME_BATCH_SIZE,
  UNIQUE_COMPONENT_NAME_MAX_SUFFIX,
} from "../StayUpToDate/componentDuplicate";
import { nextUniqueComponentName } from "../../Screens/DashboardView/StayUpToDate/AddComponentPicker/duplicateComponentPolicy";

export { isCustomShoppableCarousel };

type FromSurface = string | undefined;

function maybeMarkLinkInBioContentEdited(
  from: string | undefined,
  userId: unknown,
  username: unknown,
) {
  void markLinkInBioContentEdited(
    userId == null ? null : String(userId),
    username == null ? null : String(username),
    from,
  );
}

/**
 * When a LiB (landing-page) row is removed, also hide/delete a matching
 * website-template twin so Content Keyword / GRWM forks don’t linger as
 * Unpublished on the website or Recent Content.
 */
async function softDeleteMatchingTemplateComponentTwin(args: {
  userId: string;
  sourceRow: any;
}): Promise<void> {
  const uid = String(args.userId || "").trim();
  const row = args.sourceRow;
  if (!uid || !row) return;
  const uniqueId = String(row.unique_id || "").trim();
  const name = String(row.name || "").trim();
  const type = String(row.type || "").trim().toLowerCase();
  // Prefer unique_id; fall back to name+type for content-series / keyword rows.
  let q = supabase
    .from(TEMPLATE_COMPONENTS_TABLE)
    .select("id")
    .eq("user_id", uid)
    .neq("status", "deleted")
    .limit(5);
  if (uniqueId) {
    q = q.eq("unique_id", uniqueId);
  } else if (name) {
    q = q.eq("name", name);
    if (type) q = q.eq("type", type);
  } else {
    return;
  }
  const { data } = await q;
  for (const twin of data || []) {
    const tid = String((twin as { id?: string }).id || "").trim();
    if (!tid) continue;
    noteWebsiteTemplateDeletedId(tid);
    await supabase
      .from(TEMPLATE_COMPONENTS_TABLE)
      .update({ status: "deleted", status_expiry: "deleted" })
      .eq("id", tid)
      .eq("user_id", uid)
      .neq("status", "deleted");
  }
}

/**
 * On rename, update display title on analytics rows that belong to this
 * component (by id / unique_id). Falls back to legacy title match only when
 * the component has no id keys (old rows).
 */
async function rewriteCarouselAnalyticsTitle(opts: {
  userId: string;
  component: any;
  nextTitle: string;
  alsoSetName?: boolean;
}) {
  const { userId, component, nextTitle, alsoSetName } = opts;
  const idKeys = carouselAnalyticsLookupKeys(component).filter(
    (k) => k !== String(component?.name || "").trim(),
  );
  const { data: analytics } = await supabase
    .from("analytics")
    .select("id, details")
    .eq("user_id", userId)
    .in("name", ["Post Click", "Carousel Click"])
    .order("created_at", { ascending: false })
    .limit(2000);

  if (!analytics?.length) return;

  const prevTitle = String(component?.name || "").trim();
  const updates = analytics
    .map((row: { id: any; details: any }) => {
      const details = parseAnalyticsDetails(row.details);
      const bucketKeys = [
        String(details.component_id || "").trim(),
        String(details.unique_id || "").trim(),
        String(details.id || "").trim(),
      ].filter(Boolean);
      const matchesId =
        idKeys.length > 0 &&
        bucketKeys.some((k) => idKeys.includes(k));
      const matchesLegacyTitle =
        idKeys.length === 0 &&
        prevTitle &&
        String(details.title || "").trim() === prevTitle;
      if (!matchesId && !matchesLegacyTitle) return null;
      return {
        id: row.id,
        details: {
          ...details,
          title: nextTitle,
          ...(alsoSetName ? { name: nextTitle } : {}),
        },
      };
    })
    .filter(Boolean) as { id: any; details: Record<string, any> }[];

  if (!updates.length) return;
  void Promise.all(
    updates.map(({ id, details }) =>
      supabase
        .from("analytics")
        .update({ details: JSON.stringify(details) })
        .eq("user_id", userId)
        .eq("id", id),
    ),
  );
}

function notifyIfCampaignComponentChanged(
  result: string,
  componentType: string | undefined,
  userId: string
): string {
  if (
    result === "true" &&
    String(componentType ?? "").trim() === "campaign" &&
    userId
  ) {
    notifyCampaignsUpdated(userId);
  }
  return result;
}

function resolveBannerOriginalImage(
  type: string | undefined,
  details: string,
  originalBannerImage?: string | null
): string | null | undefined {
  const normalizedType = String(type ?? "").trim();
  if (normalizedType !== "banner" && normalizedType !== "highlight") {
    return undefined;
  }

  const explicit = String(originalBannerImage ?? "").trim();
  if (explicit) {
    return explicit;
  }

  const fromDetails = String(details ?? "").split(",")[0]?.trim();
  return fromDetails || null;
}

function withBannerOriginalImageField(
  payload: Record<string, unknown>,
  type: string | undefined,
  details: string,
  originalBannerImage?: string | null
) {
  const resolved = resolveBannerOriginalImage(type, details, originalBannerImage);
  if (resolved !== undefined) {
    payload.original_banner_image = resolved;
  }
  return payload;
}

function supportsComponentImageCrop(type: string | undefined) {
  const normalizedType = String(type ?? "").trim();
  return normalizedType === "banner" || normalizedType === "highlight";
}

/** Attach crop restore payload (`image_crop` jsonb) for banner/highlight when provided. */
function withBannerImageCropField(
  payload: Record<string, unknown>,
  type: string | undefined,
  imageCrop?: PersistedImageCrop | null
) {
  if (!supportsComponentImageCrop(type)) {
    return payload;
  }
  if (imageCrop !== undefined) {
    payload.image_crop = imageCrop;
  }
  return payload;
}

function withBannerMediaFields(
  payload: Record<string, unknown>,
  type: string | undefined,
  details: string,
  originalBannerImage?: string | null,
  imageCrop?: PersistedImageCrop | null
) {
  withBannerOriginalImageField(payload, type, details, originalBannerImage);
  withBannerImageCropField(payload, type, imageCrop);
  return payload;
}

async function syncLinkedCreatorComponentSettingsAfterUpdate({
  userId,
  from,
  previousComponent,
  nextComponent,
}: {
  userId: string;
  from?: string;
  previousComponent?: any;
  nextComponent?: any;
}) {
  if (!userId || from === "embed-search") return;

  if (isCreatorPagesMode(from)) {
    await syncLinkedCreatorComponentSettingsFromPage({
      userId,
      previousPageComponent: previousComponent,
      nextPageComponent: nextComponent,
    });
    return;
  }

  await syncLinkedCreatorComponentSettingsFromSource({
    userId,
    previousComponent,
    nextComponent,
  });
}

function getCreatorPagesScope(pageId?: string | null, component?: any) {
  return pageId || component?.page_id || getActiveCreatorPageId();
}

async function softDeleteComponentRow(
  tableName: string,
  userId: string,
  componentId: string
) {
  await supabase
    .from(tableName)
    .update({ status: "deleted", status_expiry: "deleted" })
    .eq("id", componentId)
    .eq("user_id", userId);
}

/** Soft-delete every non-deleted row with this title (active / draft / hidden). */
async function softDeleteComponentsByName(
  tableName: string,
  userId: string,
  name: string,
  pageId?: string | null
): Promise<number> {
  const trimmed =
    typeof name === "string" ? name.trim() : String(name || "").trim();
  if (!userId || !trimmed) return 0;
  let query = supabase
    .from(tableName)
    .update({ status: "deleted", status_expiry: "deleted" })
    .eq("user_id", userId)
    .eq("name", trimmed)
    .neq("status", "deleted");
  if (pageId) query = query.eq("page_id", pageId);
  const { data, error } = await query.select("id");
  if (error) return 0;
  return Array.isArray(data) ? data.length : 0;
}

function isDuplicateActiveComponentNameError(error: unknown): boolean {
  const err = error as { code?: string; message?: string } | null;
  const code = String(err?.code || "");
  const msg = String(err?.message || "");
  if (code !== "23505") return false;
  return (
    msg.includes("template_components_user_name_active_uidx") ||
    msg.includes("user_name_active") ||
    msg.includes("user_id_name") ||
    msg.includes("already exists")
  );
}

/**
 * When creating a visible carousel, remove bootstrapped hidden rows that share the
 * same name so `addComponent` is not blocked by "Duplicate Name".
 */
async function clearHiddenDuplicatesByName(
  tableName: string,
  userId: string,
  name: string,
  pageId?: string | null
): Promise<string | null> {
  let query = supabase
    .from(tableName)
    .select("id,status,status_expiry")
    .eq("user_id", userId)
    .eq("name", name)
    .neq("status", "deleted");

  if (pageId) {
    query = query.eq("page_id", pageId);
  }

  const { data } = await query;
  if (!Array.isArray(data) || data.length === 0) return null;

  let removedId: string | null = null;
  for (const row of data) {
    if (getComponentStatus(row) !== "hidden") continue;
    const id = String((row as { id?: string | number }).id ?? "");
    if (!id) continue;
    await softDeleteComponentRow(tableName, userId, id);
    removedId = id;
  }
  return removedId;
}

type RemoveHiddenCarouselBeforeCreateArgs = {
  userId: string;
  from?: string;
  componentName?: string;
  displayName?: string;
  type?: string;
  shoppableContentFilter?: string;
};

/**
 * Deletes hidden bootstrapped carousels matching the filter combination so the user
 * can create a new visible carousel with the same settings.
 */
export async function removeHiddenCarouselBeforeCreate({
  userId,
  from,
  componentName,
  displayName,
  type,
  shoppableContentFilter,
}: RemoveHiddenCarouselBeforeCreateArgs): Promise<string | null> {
  const tableName = getComponentsTableName(from);

  let query = supabase
    .from(tableName)
    .select("id,status,status_expiry,name")
    .eq("user_id", userId)
    .neq("status", "deleted");

  if (displayName) query = query.eq("display_name", displayName);
  if (type) query = query.eq("type", type);
  if (shoppableContentFilter) {
    query = query.eq("shoppable_content_filter", shoppableContentFilter);
  }

  const { data: rows } = await query;
  const hiddenRows = (Array.isArray(rows) ? rows : []).filter(
    (row) => getComponentStatus(row) === "hidden"
  );

  let removedId: string | null = null;
  for (const row of hiddenRows) {
    const id = String((row as { id?: string | number }).id ?? "");
    if (!id) continue;
    await softDeleteComponentRow(tableName, userId, id);
    removedId = id;
  }

  if (!removedId && componentName) {
    removedId = await clearHiddenDuplicatesByName(
      tableName,
      userId,
      componentName,
      isCreatorPagesMode(from) ? getActiveCreatorPageId() : null
    );
  }

  return removedId;
}

async function swapCarouselIdInCollections(
  tableName: string,
  userId: string,
  fromId: string,
  toId: string
) {
  if (!fromId || !toId || fromId === toId) return;

  const { data: collections } = await supabase
    .from(tableName)
    .select("id,post_ids")
    .eq("user_id", userId)
    .eq("type", "carousel-collection")
    .neq("status", "deleted");

  if (!Array.isArray(collections) || collections.length === 0) return;

  await Promise.all(
    collections.map(async (collection: any) => {
      const ids = String(collection?.post_ids || "")
        .split(",")
        .map((id: string) => id.trim())
        .filter(Boolean);
      if (!ids.includes(String(fromId))) return;

      const replaced = ids.map((id: string) =>
        id === String(fromId) ? String(toId) : id
      );
      const deduped = Array.from(new Set(replaced));

      await supabase
        .from(tableName)
        .update({ post_ids: deduped.join(",") })
        .eq("id", collection.id)
        .eq("user_id", userId);
    })
  );
}

/**
 * On update, a visible carousel may take a reserved title still held by a hidden row.
 * Remove hidden conflicts and keep collection post_ids pointing at the edited carousel.
 */
async function resolveHiddenNameConflictsForUpdate(
  tableName: string,
  userId: string,
  name: string,
  currentComponentId: string,
  pageId?: string | null
): Promise<boolean> {
  let query = supabase
    .from(tableName)
    .select("id,status,status_expiry")
    .eq("user_id", userId)
    .eq("name", name)
    .neq("id", currentComponentId)
    .neq("status", "deleted");

  if (pageId) {
    query = query.eq("page_id", pageId);
  }

  const { data: conflicts } = await query;
  if (!Array.isArray(conflicts) || conflicts.length === 0) {
    return true;
  }

  // Only remove hidden bootstrap duplicates — visible duplicate titles are allowed.
  for (const row of conflicts) {
    if (getComponentStatus(row) !== "hidden") continue;
    const hiddenId = String((row as { id?: string | number }).id ?? "");
    if (!hiddenId) continue;
    await swapCarouselIdInCollections(
      tableName,
      userId,
      hiddenId,
      String(currentComponentId)
    );
    await softDeleteComponentRow(tableName, userId, hiddenId);
  }

  return true;
}

async function assertInsertAllowedOrClearHiddenByName(
  tableName: string,
  userId: string,
  name: string,
  pageId?: string | null
): Promise<{ allowed: boolean; removedHiddenId: string | null }> {
  let query = supabase
    .from(tableName)
    .select("id,status,status_expiry")
    .eq("user_id", userId)
    .eq("name", name)
    .neq("status", "deleted");

  if (pageId) {
    query = query.eq("page_id", pageId);
  }

  const { data } = await query;
  if (!Array.isArray(data) || data.length === 0) {
    return { allowed: true, removedHiddenId: null };
  }

  // Duplicate visible titles are allowed — only clear hidden bootstrap rows.
  void data.some((row) => getComponentStatus(row) !== "hidden");

  const removedHiddenId = await clearHiddenDuplicatesByName(
    tableName,
    userId,
    name,
    pageId
  );
  return { allowed: true, removedHiddenId };
}

const trimComponentName = (name: any) =>
  typeof name === "string" ? name.trim() : name;

async function getNextLiveIndex(
  table: "stayuptodate_components" | "embed_components" | "template_components" | "page_components" | string,
  user_id: any,
  pageId?: string | null
): Promise<number> {
  // Count-based indices break when legacy rows have very large `index` values.
  // Always append at max(index)+1 so new components appear at the top (since fetch orders by index DESC).
  try {
    let query = supabase
      .from(table)
      .select("index")
      .eq("user_id", user_id)
      .neq("status", "deleted")
      .order("index", { ascending: false })
      .limit(1);
    if (pageId) query = query.eq("page_id", pageId);
    const { data, error } = await query;
    if (error) throw error;
    const maxIndexRaw = data?.[0]?.index;
    const maxIndex = typeof maxIndexRaw === "number" ? maxIndexRaw : Number(maxIndexRaw ?? -1);
    return Number.isFinite(maxIndex) ? maxIndex + 1 : 0;
  } catch (e) {
    // Fallback to count if query fails for any reason.
    let countQuery = supabase
      .from(table)
      .select("id")
      .eq("user_id", user_id)
      .neq("status", "deleted");
    if (pageId) countQuery = countQuery.eq("page_id", pageId);
    const { data: count } = await countQuery;
    return count ? count.length : 0;
  }
}

/** PostgREST: column missing on template_components / older DBs. */
function isMissingOriginalBannerImageColumnError(error: unknown): boolean {
  const message = String(
    (error as { message?: string; details?: string; hint?: string })?.message ||
      (error as { details?: string })?.details ||
      (error as { hint?: string })?.hint ||
      "",
  ).toLowerCase();
  if (!message.includes("original_banner_image")) return false;
  return (
    message.includes("schema cache") ||
    message.includes("could not find") ||
    message.includes("does not exist") ||
    message.includes("not found")
  );
}

function omitOriginalBannerImage(
  row: Record<string, unknown>,
): Record<string, unknown> {
  const { original_banner_image: _omit, ...rest } = row;
  return rest;
}

function withDescriptionField(
  payload: Record<string, unknown>,
  description?: string
) {
  if (carouselSupportsDescription(payload.type as string)) {
    payload.description = trimCarouselDescription(description || "");
  }
  return payload;
}

function isMissingDescriptionColumnError(error: any) {
  const message = String(
    error?.message || error?.details || error?.hint || ""
  ).toLowerCase();
  return (
    message.includes("description") &&
    (message.includes("column") ||
      message.includes("schema cache") ||
      message.includes("could not find"))
  );
}

function omitDescriptionField(payload: Record<string, unknown>) {
  const { description: _omit, ...rest } = payload;
  return rest;
}

async function insertComponentRow(
  table: "stayuptodate_components" | "embed_components" | "template_components" | "page_components" | string,
  payload: Record<string, unknown>,
  archiveFields: Record<string, unknown>,
): Promise<string | null> {
  const withArchive = { ...payload, ...archiveFields };
  const tryInsert = async (row: Record<string, unknown>) => {
    const { data, error } = await supabase
      .from(table)
      .insert([row])
      .select("id")
      .maybeSingle();
    if (error) return { error, id: null as string | null };
    const id = (data as { id?: string | number } | null)?.id;
    return {
      error: null,
      id: id == null ? null : String(id),
    };
  };

  let result = await tryInsert(withArchive);
  if (result.error && isMissingArchiveColumnError(result.error)) {
    result = await tryInsert(payload);
  }
  if (result.error && isMissingImageCropColumnError(result.error) && "image_crop" in payload) {
    const { image_crop: _omit, ...withoutCrop } = withArchive;
    const { image_crop: _omit2, ...payloadWithoutCrop } = payload;
    result = await tryInsert(withoutCrop);
    if (result.error && isMissingArchiveColumnError(result.error)) {
      result = await tryInsert(payloadWithoutCrop);
    }
  }
  // template_components (and some embeds) may lack original_banner_image —
  // image still lives in details[0] for banner/highlight.
  if (
    result.error &&
    isMissingOriginalBannerImageColumnError(result.error) &&
    "original_banner_image" in payload
  ) {
    const withoutBannerArchive = omitOriginalBannerImage(withArchive);
    const withoutBannerPayload = omitOriginalBannerImage(payload);
    if ("image_crop" in withoutBannerArchive) {
      const { image_crop: _c1, ...noCropArchive } = withoutBannerArchive;
      const { image_crop: _c2, ...noCropPayload } = withoutBannerPayload;
      result = await tryInsert(noCropArchive);
      if (result.error && isMissingArchiveColumnError(result.error)) {
        result = await tryInsert(noCropPayload);
      }
    } else {
      result = await tryInsert(withoutBannerArchive);
      if (result.error && isMissingArchiveColumnError(result.error)) {
        result = await tryInsert(withoutBannerPayload);
      }
    }
  }
  if (result.error && isMissingDescriptionColumnError(result.error) && "description" in payload) {
    result = await tryInsert(omitDescriptionField(withArchive));
    if (result.error && isMissingArchiveColumnError(result.error)) {
      result = await tryInsert(omitDescriptionField(payload));
    }
  }

  if (result.error) {
    throw result.error;
  }
  return result.id;
}

const getComponentRecordId = (component: any) =>
  String(component?.id ?? component?.component_id ?? "").trim();

const isSameComponentName = (nextName: any, currentName: any) => {
  const left = trimComponentName(nextName);
  const right = trimComponentName(currentName);
  if (!left || !right) return false;
  return left.toLowerCase() === right.toLowerCase();
};

export async function addEmbedComponent(
  name: any,
  details: any,
  username: any,
  user_id: any,
  isDays: any,
  cta: any,
  query: any,
  displayName?: any,
  type?: any,
  postIds?: any,
  content_order?: any,
  content_shape?: any,
  content_sound?: any,
  shoppable_content_filter?: any,
  customOrderPostIds?: any,
  shoppableFilter?: string,
  statusExpiry?: string,
  originalBannerImage?: string | null,
  imageCrop?: PersistedImageCrop | null,
  description?: string
) {
  name = trimComponentName(name);
  type = type || "";
  const { allowed } = await assertInsertAllowedOrClearHiddenByName(
    "embed_components",
    user_id,
    name
  );
  if (!allowed) {
    return "Duplicate Name";
  }

  {
    const nextIndex = await getNextLiveIndex("embed_components", user_id);
    const resolvedStatus = statusExpiry || "active";
    const archiveIndex =
      resolvedStatus === "archive"
        ? await getNextArchiveIndex(user_id, "embed-search")
        : 0;
    const archiveFields = getArchiveFieldsForInsert(
      resolvedStatus,
      archiveIndex
    );
    const uniqueSlug = createComponentSlug(name);
    await insertComponentRow("embed_components", withDescriptionField(withBannerMediaFields({
      name,
      details,
      user_id,
      username,
      index: nextIndex,
      type,
      unique_id: uniqueSlug,
      route_name: uniqueSlug,
      post_ids: postIds,
      display_name: displayName,
      cta_placeholder: cta,
      query: query,
      content_order,
      content_shape,
      content_sound,
      shoppable_content_filter: shoppable_content_filter || "both",
      status: resolvedStatus,
      status_expiry: resolvedStatus,
      custom_order: customOrderPostIds,
      shoppable_status_filter: shoppableFilter || "all",
    }, type, details, originalBannerImage, imageCrop), description), archiveFields);

    return notifyIfCampaignComponentChanged("true", type, user_id);
  }
}

export async function addComponent(
  name: any,
  details: any,
  username: any,
  user_id: any,
  isDays: any,
  cta: any,
  query: any,
  displayName?: any,
  type?: any,
  postIds?: any,
  content_order?: any,
  content_shape?: any,
  content_sound?: any,
  shoppable_content_filter?: any,
  from?: string,
  customOrderPostIds?: any,
  shoppableFilter?: string,
  pageIdOrStatus?: string | null,
  statusExpiryOrPageId?: string,
  originalBannerImage?: string | null,
  imageCrop?: PersistedImageCrop | null,
  description?: string,
  capture?: { insertedRowId?: string | null },
) {
  name = trimComponentName(name);
  const statusValues = new Set(["active", "draft", "archive", "hidden", "deleted"]);
  const firstArg = String(pageIdOrStatus || "").trim().toLowerCase();
  const secondArg = String(statusExpiryOrPageId || "").trim().toLowerCase();
  const firstIsStatus = statusValues.has(firstArg);
  const secondIsStatus = statusValues.has(secondArg);

  // Backward-compatible argument resolution:
  // Old call sites passed `statusExpiry` as the last arg.
  // New creator-pages call sites may pass both `pageId` and `statusExpiry`.
  const resolvedPageId = firstIsStatus
    ? statusExpiryOrPageId || null
    : pageIdOrStatus || null;
  const resolvedStatusExpiry = firstIsStatus
    ? firstArg || "active"
    : secondIsStatus
      ? secondArg
      : "active";

  if (from === "embed-search") {
    return addEmbedComponent(
      name,
      details,
      username,
      user_id,
      isDays,
      cta,
      query,
      displayName,
      type,
      postIds,
      content_order,
      content_shape,
      content_sound,
      shoppable_content_filter,
      customOrderPostIds,
      shoppableFilter,
      resolvedStatusExpiry,
      originalBannerImage,
      imageCrop,
      description
    );
  } else if (isCreatorPagesMode(from)) {
    type = type || "";
    const scopedPageId = getCreatorPagesScope(resolvedPageId);

    if (!scopedPageId) {
      throw new Error("No active creator page selected");
    }

    const tableName = getComponentsTableName(from);
    const { allowed } = await assertInsertAllowedOrClearHiddenByName(
      tableName,
      user_id,
      name,
      scopedPageId
    );
    if (!allowed) {
      return "Duplicate Name";
    }

    {
      const { data: count } = await supabase
        .from(tableName)
        .select("id")
        .eq("user_id", user_id)
        .eq("page_id", scopedPageId)
        .neq("status", "deleted");

      const uniqueSlug = createComponentSlug(name);
      const resolvedStatus = resolvedStatusExpiry || "active";
      let { error } = await supabase.from(tableName).insert([
        withDescriptionField(withBannerMediaFields({
          name,
          details,
          user_id,
          username,
          page_id: scopedPageId,
          index: count ? count.length : 0,
          type,
          unique_id: uniqueSlug,
          route_name: uniqueSlug,
          post_ids: postIds,
          display_name: displayName,
          cta_placeholder: cta || "",
          query,
          content_order,
          content_shape,
          content_sound,
          status: resolvedStatus,
          status_expiry: resolvedStatus,
          shoppable_content_filter: shoppable_content_filter || "both",
          custom_order: customOrderPostIds,
          shoppable_status_filter: shoppableFilter || "all",
        }, type, details, originalBannerImage, imageCrop), description),
      ]);

      if (error && isMissingImageCropColumnError(error) && imageCrop) {
        ({ error } = await supabase.from(tableName).insert([
          withDescriptionField(withBannerOriginalImageField({
            name,
            details,
            user_id,
            username,
            page_id: scopedPageId,
            index: count ? count.length : 0,
            type,
            unique_id: uniqueSlug,
            route_name: uniqueSlug,
            post_ids: postIds,
            display_name: displayName,
            cta_placeholder: cta || "",
            query,
            content_order,
            content_shape,
            content_sound,
            status: resolvedStatus,
            status_expiry: resolvedStatus,
            shoppable_content_filter: shoppable_content_filter || "both",
            custom_order: customOrderPostIds,
            shoppable_status_filter: shoppableFilter || "all",
          }, type, details, originalBannerImage), description),
        ]));
      }

      if (error && isMissingDescriptionColumnError(error)) {
        ({ error } = await supabase.from(tableName).insert([
          omitDescriptionField(withBannerMediaFields({
            name,
            details,
            user_id,
            username,
            page_id: scopedPageId,
            index: count ? count.length : 0,
            type,
            unique_id: uniqueSlug,
            route_name: uniqueSlug,
            post_ids: postIds,
            display_name: displayName,
            cta_placeholder: cta || "",
            query,
            content_order,
            content_shape,
            content_sound,
            status: resolvedStatus,
            status_expiry: resolvedStatus,
            shoppable_content_filter: shoppable_content_filter || "both",
            custom_order: customOrderPostIds,
            shoppable_status_filter: shoppableFilter || "all",
          }, type, details, originalBannerImage, imageCrop)),
        ]));
      }

      if (
        error &&
        isMissingOriginalBannerImageColumnError(error)
      ) {
        const baseRow: Record<string, unknown> = {
          name,
          details,
          user_id,
          username,
          page_id: scopedPageId,
          index: count ? count.length : 0,
          type,
          unique_id: uniqueSlug,
          route_name: uniqueSlug,
          post_ids: postIds,
          display_name: displayName,
          cta_placeholder: cta || "",
          query,
          content_order,
          content_shape,
          content_sound,
          status: resolvedStatus,
          status_expiry: resolvedStatus,
          shoppable_content_filter: shoppable_content_filter || "both",
          custom_order: customOrderPostIds,
          shoppable_status_filter: shoppableFilter || "all",
        };
        ({ error } = await supabase.from(tableName).insert([baseRow]));
      }

      if (error && isDuplicateActiveComponentNameError(error)) {
        throw error;
      }

      if (error) {
        throw error;
      }

      return notifyIfCampaignComponentChanged("true", type, user_id);
    }
  } else {
    type = type || "";
    // landing-page → stayuptodate_components; website-template → template_components
    const tableName = getComponentsTableName(from);

    const { allowed } = await assertInsertAllowedOrClearHiddenByName(
      tableName,
      user_id,
      name
    );
    if (!allowed) {
      return "Duplicate Name";
    }

    {
      const nextIndex = await getNextLiveIndex(tableName, user_id);
      const resolvedStatus = isWebsiteLibPersistGated(from)
        ? LIB_PENDING_PUBLISH_STATUS
        : resolvedStatusExpiry
          ? resolvedStatusExpiry
          : from === "website-template"
            ? "draft"
            : "active";
      const archiveIndex =
        resolvedStatus === "archive"
          ? await getNextArchiveIndex(user_id, from)
          : 0;
      const archiveFields = getArchiveFieldsForInsert(
        resolvedStatus,
        archiveIndex
      );
      // Opaque slug so same-title / same-controls carousels stay distinct
      // (website inserts previously fell back to display name → first match wins).
      const uniqueSlug = createComponentSlug(name);
      const insertedRowId = await insertComponentRow(
        tableName,
        withDescriptionField(withBannerMediaFields({
          name,
          details,
          user_id,
          username,
          index: nextIndex,
          type,
          unique_id: uniqueSlug,
          route_name: uniqueSlug,
          post_ids: postIds,
          display_name: displayName,
          cta_placeholder: cta || "",
          query: query,
          content_order,
          content_shape,
          content_sound,
          status: resolvedStatus,
          status_expiry: resolvedStatus,
          shoppable_content_filter: shoppable_content_filter || "both",
          custom_order: customOrderPostIds,
          shoppable_status_filter: shoppableFilter || "all",
        }, type, details, originalBannerImage, imageCrop), description),
        archiveFields
      );
      if (capture) {
        capture.insertedRowId = insertedRowId;
      }

      maybeMarkLinkInBioContentEdited(from, user_id, username);
      if (isWebsiteLibPersistGated(from) && user_id) {
        await noteWebsiteLibCreatedRow({
          userId: String(user_id),
          name: String(name || ""),
          type: String(type || ""),
        });
      }
      return notifyIfCampaignComponentChanged("true", type, user_id);
    }
  }
}

export async function updateComponent(
  name: any,
  component: any,
  username: any,
  user_id: any,
  query: any,
  type: any,
  postIds: any,
  details: any,

  removePostIds?: any,
  content_order?: any,
  content_shape?: any,
  content_sound?: any,
  shoppable_content_filter?: any,
  from?: any,
  customOrderPostIds?: any,
  pinnedPostIds?: string | null,
  shoppableFilter?: string
) {
  name = trimComponentName(name);
  type = type || "";
  const tableName = getComponentsTableName(from);
  const scopedPageId = getCreatorPagesScope(undefined, component);

  const componentId = getComponentRecordId(component);
  if (shouldDeferWebsiteLibWrite(from, componentId || component?.id)) {
    applyWebsiteLibLocalPatch(componentId || component?.id, {
      name,
      post_ids: postIds,
      query,
      details,
      removed_postids: removePostIds,
      content_order,
      content_shape,
      content_sound,
      shoppable_content_filter,
      custom_order: customOrderPostIds,
      pinned_posts: pinnedPostIds || null,
      shoppable_status_filter: shoppableFilter || "all",
    });
    return "true";
  }
  const isRenaming = !isSameComponentName(name, component?.name);

  // Uniqueness is only enforced when the title changes. Product/settings-only
  // saves keep the same name and must not hit duplicate-title checks.
  if (isRenaming) {
    if (!componentId && !component?.id) {
      return "Couldn't update that title.";
    }
    await resolveHiddenNameConflictsForUpdate(
      tableName,
      user_id,
      name,
      componentId || String(component.id),
      isCreatorPagesMode(from) ? scopedPageId : null,
    );
  }

  {
    let updateError = null;
    const nextDescription = carouselSupportsDescription(type || component?.type)
      ? trimCarouselDescription(
          typeof component?.description === "string" ? component.description : ""
        )
      : undefined;
    const updatePayload: Record<string, unknown> = {
        name,
        post_ids: postIds,
        query,
        removed_postids: removePostIds,
        details,
        content_order,
        content_shape,
        content_sound,
        shoppable_content_filter,
        custom_order: customOrderPostIds,
        pinned_posts: pinnedPostIds || null,
        shoppable_status_filter: shoppableFilter || "all",
        type: type || component?.type,
        display_name: component?.display_name,
    };
    if (nextDescription !== undefined) {
      updatePayload.description = nextDescription;
    }
    if(component.display_name === "reels"){
      const { error } = await supabase
      .from(tableName)
      .update(updatePayload)
      .eq("user_id", user_id)
      .eq("id", component.id);
      updateError = error;
    }
    else{
    const { error } = await supabase
      .from(tableName)
      .update(updatePayload)
      .eq("user_id", user_id)
      .eq("id", component.id);
      updateError = error;
    }

    if (updateError && isMissingDescriptionColumnError(updateError) && nextDescription !== undefined) {
      const withoutDescription = omitDescriptionField(updatePayload);
      const { error } = await supabase
        .from(tableName)
        .update(withoutDescription)
        .eq("user_id", user_id)
        .eq("id", component.id);
      updateError = error;
    }

    if (updateError) {
      throw updateError;
    }

    const nextComponentState = {
      ...component,
      name,
      post_ids: postIds,
      query,
      removed_postids: removePostIds,
      details,
      content_order,
      content_shape,
      content_sound,
      shoppable_content_filter,
      custom_order: customOrderPostIds,
      pinned_posts: pinnedPostIds || null,
      shoppable_status_filter: shoppableFilter || "all",
      type: type || component?.type,
      display_name: component?.display_name,
      ...(nextDescription !== undefined ? { description: nextDescription } : {}),
    };

    if (name !== component.name) {
      if (isCreatorPagesMode(from)) {
        await syncSourceComponentMetadataFromPageComponent({
          userId: user_id,
          previousPageComponent: component,
          nextPageComponent: nextComponentState,
        });
      } else {
        await syncCreatorPageMetadataFromSourceComponent({
          userId: user_id,
          previousComponent: component,
          nextComponent: nextComponentState,
        });
      }
    }

    await syncLinkedCreatorComponentSettingsAfterUpdate({
      userId: user_id,
      from,
      previousComponent: component,
      nextComponent: nextComponentState,
    });

    if (name !== component.name && !isCreatorPagesMode(from)) {
      void rewriteCarouselAnalyticsTitle({
        userId: user_id,
        component,
        nextTitle: name,
      });
    }

    if (type === "product-bank") {
      const { data, error } = await supabase.rpc(
        "querybankpostss",
        // "get_queryproductbankpostttsssss",
        {
          username,
          query_string: "",
          userid: user_id,
          title: component?.name,
          query_tagg: "",
        }
      );
      if (data?.length) {
        const updatePromises = data.flatMap((i: any) => {
          const regex = new RegExp(`\\|${component?.name}\\|`, "g");
          const table_query = i.table_query.replace(regex, `|${name}|`);

          // Create promises for both updates
          const productBankUpdate = supabase
            .from("product_bank")
            .update({ query: table_query })
            .eq("user_id", user_id)
            .eq("product_id", i.table_id);

          const usernameUpdate = supabase
            .from(username)
            .update({ metadata: table_query })
            .eq("user_id", user_id)
            .eq("post_id", i.table_id);

          return [productBankUpdate, usernameUpdate]; // Return both promises
        });

        // Execute all update promises
        await Promise.all(updatePromises);
      }

      // if (data?.length) {
      //   const updatePromises = data.map((i: any) => {
      //     const regex = new RegExp(`\\|${component?.name}\\|`, "g");
      //     const table_query = i.table_query.replace(regex, `|${name}|`);
      //     return supabase
      //       .from("product_bank")
      //       .update({ query: table_query })
      //       .eq("user_id", user_id)
      //       .eq("product_id", i.table_id);
      //       return supabase
      //       .from(username)
      //       .update({ metadata: table_query })
      //       .eq("user_id", user_id)
      //       .eq("post_id", i.table_id);
      //   });
      //   await Promise.all(updatePromises);
      // }
    }

    maybeMarkLinkInBioContentEdited(from, user_id, username);
    return "true";
  }
}
export async function updateComponentSingle(
  name: any,
  component: any,
  details: any,
  username: any,
  user_id: any,
  cta?: any,
  type?: any,
  postIds?: any,
  from?: any,
  originalBannerImage?: string | null,
  imageCrop?: PersistedImageCrop | null
) {
  type = type || "";
  const tableName = getComponentsTableName(from);
  const scopedPageId = getCreatorPagesScope(undefined, component);
  const componentId = getComponentRecordId(component);
  if (shouldDeferWebsiteLibWrite(from, componentId || component?.id)) {
    applyWebsiteLibLocalPatch(componentId || component?.id, {
      details,
      name,
      cta_placeholder: cta,
      ...(postIds != null ? { post_ids: postIds } : {}),
      ...(originalBannerImage != null
        ? { original_banner_image: originalBannerImage }
        : {}),
      ...(imageCrop !== undefined ? { image_crop: imageCrop } : {}),
    });
    return "true";
  }
  const isRenaming = !isSameComponentName(name, component?.name);

  if (isRenaming) {
    const resolvedId = componentId || String(component?.id || "");
    if (resolvedId) {
      await resolveHiddenNameConflictsForUpdate(
        tableName,
        user_id,
        name,
        resolvedId,
        isCreatorPagesMode(from) ? scopedPageId : null,
      );
    }
  }

  {
    const resolvedOriginalBannerImage = resolveBannerOriginalImage(
      type,
      details,
      originalBannerImage ?? component?.original_banner_image
    );
    const updatePayload: Record<string, unknown> = {
      details,
      name,
      cta_placeholder: cta,
      type: type || component?.type,
      display_name:
        type === "latest-podcast" ? "latest-podcast" : component?.display_name,
    };
    if (resolvedOriginalBannerImage !== undefined) {
      updatePayload.original_banner_image = resolvedOriginalBannerImage;
    }
    if (supportsComponentImageCrop(type) && imageCrop !== undefined) {
      updatePayload.image_crop = imageCrop;
    }

    let { data, error } = await supabase
      .from(tableName)
      .update(updatePayload)
      .eq("user_id", user_id)
      .eq("id", component.id)
      .neq("status", "deleted");

    if (error && isMissingImageCropColumnError(error) && "image_crop" in updatePayload) {
      const { image_crop: _omit, ...withoutCrop } = updatePayload;
      ({ data, error } = await supabase
        .from(tableName)
        .update(withoutCrop)
        .eq("user_id", user_id)
        .eq("id", component.id)
        .neq("status", "deleted"));
    }

    if (
      error &&
      isMissingOriginalBannerImageColumnError(error) &&
      "original_banner_image" in updatePayload
    ) {
      const withoutBanner = omitOriginalBannerImage(updatePayload);
      const { image_crop: _crop, ...withoutBannerAndCrop } = withoutBanner as Record<
        string,
        unknown
      > & { image_crop?: unknown };
      const retryPayload =
        "image_crop" in withoutBanner ? withoutBannerAndCrop : withoutBanner;
      ({ data, error } = await supabase
        .from(tableName)
        .update(retryPayload)
        .eq("display_name", component.display_name)
        .eq("type", component.type)
        .eq("user_id", user_id)
        .eq("id", component.id)
        .neq("status", "deleted"));
    }

    if (error) {
      throw error;
    }

    const nextComponentState = {
      ...component,
      name,
      details,
      cta_placeholder: cta,
      type: type || component?.type,
      display_name:
        type === "latest-podcast" ? "latest-podcast" : component?.display_name,
      ...(resolvedOriginalBannerImage !== undefined
        ? { original_banner_image: resolvedOriginalBannerImage }
        : {}),
      ...(imageCrop !== undefined ? { image_crop: imageCrop } : {}),
    };

    if (name !== component.name) {
      if (isCreatorPagesMode(from)) {
        await syncSourceComponentMetadataFromPageComponent({
          userId: user_id,
          previousPageComponent: component,
          nextPageComponent: nextComponentState,
        });
      } else {
        await syncCreatorPageMetadataFromSourceComponent({
          userId: user_id,
          previousComponent: component,
          nextComponent: nextComponentState,
        });
      }
    }

    await syncLinkedCreatorComponentSettingsAfterUpdate({
      userId: user_id,
      from,
      previousComponent: component,
      nextComponent: nextComponentState,
    });

    if (name !== component.name && !isCreatorPagesMode(from)) {
      void rewriteCarouselAnalyticsTitle({
        userId: user_id,
        component,
        nextTitle: name,
        alsoSetName: true,
      });
    }
    maybeMarkLinkInBioContentEdited(from, user_id, username);
    return "true";
  }
}

export async function updateIndexValue(
  user_id: any,
  newData: any,
  from?: string,
  pageId?: string | null
) {
  if (isWebsiteLibPersistGated(from)) {
    markWebsiteLibDirty();
    return;
  }
  const tableName = getComponentsTableName(from);
  const scopedPageId = getCreatorPagesScope(pageId, newData?.[0]);
  const results = await Promise.all(
    (Array.isArray(newData) ? newData : []).map((i: any, idx: number) => {
      const descendingIdx = newData.length - 1 - idx;
      let query = supabase
        .from(tableName)
        .update({ index: descendingIdx })
        .eq("user_id", user_id)
        .neq("status", "deleted");

      // Prefer id — name-based updates hit every duplicate title and can
      // scramble / wipe sibling carousels after a single delete.
      if (i?.id != null && i.id !== "") {
        query = query.eq("id", i.id);
      } else {
        query = query.eq("name", i.name);
      }

      if (isCreatorPagesMode(from) && scopedPageId) {
        query = query.eq("page_id", scopedPageId);
      }

      return query;
    }),
  );
  const errorArray = results
    .map((r) => r.error?.message)
    .filter(Boolean) as string[];
  if (errorArray.length) {
    console.error("updateIndexValue errors:", errorArray);
  }
  maybeMarkLinkInBioContentEdited(from, user_id, null);
}
export async function updateEmbedIndexValue(user_id: any, newData: any) {
  return updateIndexValue(user_id, newData, "embed-search");
}

function isLiveDashboardComponent(row: any) {
  const status = getComponentStatus(row);
  return status !== "deleted" && status !== "hidden" && status !== "archive";
}

/**
 * Shift live rows at or above `sourceIndex` up by 1 so the copy can keep
 * that index and sit directly below the source. One lean select + targeted
 * updates — no full list rewrite.
 */
async function placeLiveComponentBelowSource({
  tableName,
  userId,
  pageId,
  sourceIndex,
  excludeId,
}: {
  tableName: string;
  userId: string;
  pageId?: string | null;
  sourceIndex: number;
  excludeId: string;
}) {
  let query = supabase
    .from(tableName)
    .select("id,index,status,status_expiry")
    .eq("user_id", userId)
    .neq("status", "deleted")
    .neq("id", excludeId)
    .gte("index", sourceIndex);

  if (pageId) {
    query = query.eq("page_id", pageId);
  }

  const { data, error } = await query;
  if (error) throw error;

  const toBump = (Array.isArray(data) ? data : []).filter(isLiveDashboardComponent);
  if (toBump.length === 0) return;

  const results = await Promise.all(
    toBump.map((row) =>
      supabase
        .from(tableName)
        .update({ index: Number(row.index) + 1 })
        .eq("id", row.id)
        .eq("user_id", userId)
    )
  );

  const firstError = results.find((result) => result.error)?.error;
  if (firstError) throw firstError;
}

async function writeArchiveIndexValues(
  user_id: any,
  newData: any[],
  from?: FromSurface
) {
  const table = getArchiveTableName(from);
  for (const [idx, i] of newData.entries()) {
    const descendingIdx = newData.length - 1 - idx;
    let query = supabase
      .from(table)
      .update({ archive_index: descendingIdx })
      .eq("user_id", user_id)
      .neq("status", "deleted");
    if (i?.id != null && i.id !== "") {
      query = query.eq("id", i.id);
    } else {
      query = query.eq("name", i.name);
    }
    const { error } = await query;

    if (error) {
      if (isMissingArchiveColumnError(error)) {
        return false;
      }
      throw error;
    }
  }
  return true;
}

/** Reorder archived components using `archive_index` (falls back to legacy `index` updates). */
export async function updateArchiveIndexValue(
  user_id: any,
  newData: any,
  from?: FromSurface
) {
  if (isWebsiteLibPersistGated(from)) {
    markWebsiteLibDirty();
    return;
  }
  if (!Array.isArray(newData) || newData.length === 0) return;

  const wroteArchiveIndex = await writeArchiveIndexValues(
    user_id,
    newData,
    from
  );
  if (wroteArchiveIndex) return;

  if (from === "embed-search") {
    await updateEmbedIndexValue(user_id, newData);
  } else {
    await updateIndexValue(user_id, newData);
  }
}

async function reindexRemainingComponents(
  user_id: any,
  data: any[],
  from?: FromSurface,
  reindexMode: "live" | "archive" = "live"
) {
  if (!Array.isArray(data) || data.length === 0) return;

  if (reindexMode === "archive") {
    await updateArchiveIndexValue(user_id, data, from);
    return;
  }

  if (from === "embed-search") {
    await updateEmbedIndexValue(user_id, data);
  } else {
    await updateIndexValue(user_id, data);
  }
}

export async function deleteComponent(
  name: any,
  user_id: any,
  data: any,
  dataToDelete: any,
  refetch: any,
  username: any,
  from?: string,
  pageId?: string | null,
  reindexMode: "live" | "archive" = "live"
) {
  let deletedLinkedCreatorPageId: string | null = null;
  let query = dataToDelete?.[0]?.name;
  let type = dataToDelete?.[0]?.type;
  let unique_id = dataToDelete?.[0]?.unique_id;
  const tableName = getComponentsTableName(from);
  const deleteId = dataToDelete?.[0]?.id;
  // LiB publish gate: unpublished soft-adds (session-tracked OR pending_publish
  // status) hard-delete immediately — they were never live publicly.
  const pendingUnpublished =
    isLibPendingPublishComponent(dataToDelete?.[0]) ||
    String(dataToDelete?.[0]?.status_expiry ?? dataToDelete?.[0]?.status ?? "")
      .toLowerCase()
      .trim() === LIB_PENDING_PUBLISH_STATUS;
  if (
    isWebsiteLibPersistGated(from) &&
    (isWebsiteLibSessionCreated(deleteId) || pendingUnpublished)
  ) {
    await hardDeleteWebsiteLibSessionCreatedRow(user_id, deleteId);
    // Also drop a forked website-template twin (content-keyword / content-series
    // often exists in both stayuptodate_components and template_components).
    await softDeleteMatchingTemplateComponentTwin({
      userId: user_id,
      sourceRow: dataToDelete?.[0],
    });
    maybeMarkLinkInBioContentEdited(from, user_id, username);
    if (typeof refetch === "function") refetch();
    return null;
  }
  if (shouldDeferWebsiteLibWrite(from, deleteId)) {
    // Soft-remove live row — keep status/schedule; hide in editor until Publish.
    noteWebsiteLibDeletedId(deleteId);
    await softDeleteMatchingTemplateComponentTwin({
      userId: user_id,
      sourceRow: dataToDelete?.[0],
    });
    return null;
  }
  // Immediate deletes: still tombstone so editor sync cannot resurrect mid-refetch.
  if (deleteId != null && deleteId !== "") {
    noteWebsiteLibDeletedId(deleteId);
  }
  const scopedPageId = getCreatorPagesScope(pageId, dataToDelete?.[0]);
  if (type === "product-bank") {
    const { data: products, error }: any = await supabase
      .from("product_bank")
      .select("*")
      .eq("user_id", user_id)
      .ilike("query", `%|${query}|%`);
    for (let p of products) {
      let updatedData = p.query
        ?.replace(/\|/g, "") // Remove all pipes
        .split(",") // Split by comma
        .filter((item: any) => {
          return item !== query;
        }) // Filter out the `query` item
        .map((item: any) => `|${item}|`) // Add pipes around each item
        .join(","); // Join items back into a string
      const { data, error } = await supabase
        .from("product_bank")
        .update({ query: updatedData })
        .eq("user_id", user_id)
        .eq("product_id", p.product_id);
      await supabase
        .from(username)
        .update({ metadata: updatedData })
        .eq("user_id", user_id)
        .eq("post_id", p.product_id);
    }
  }
  if (type === "links-section") {
    await softDeleteUserLinksForLinksSection({
      user_id,
      componentId: String(deleteId || "").trim(),
      collectionName: String(name || query || "").trim(),
      from,
    });
  }

  if (
    !isCreatorPagesMode(from) &&
    from !== "embed-search" &&
    dataToDelete?.[0]
  ) {
    deletedLinkedCreatorPageId = await deleteLinkedCreatorPageForSourceComponent({
      userId: user_id,
      sourceComponent: dataToDelete[0],
    });
  }

  if (isCreatorPagesMode(from) && scopedPageId && dataToDelete?.[0]?.id) {
    await supabase
      .from("creator_pages")
      .update({ expanded_component_id: null })
      .eq("id", scopedPageId)
      .eq("expanded_component_id", dataToDelete[0].id);
  }

  updateShopLink(unique_id, username,user_id, tableName);
  if (deleteId == null || deleteId === "") {
    throw new Error("Cannot remove component: missing id");
  }
  let deleteQuery = supabase
    .from(tableName)
    .update({ status: "deleted", status_expiry: "deleted" })
    .eq("id", deleteId)
    .eq("user_id", user_id)
    .neq("status", "deleted");

  if (isCreatorPagesMode(from) && scopedPageId) {
    deleteQuery = deleteQuery.eq("page_id", scopedPageId);
  }

  const { error: deleteError } = await deleteQuery;
  if (deleteError) {
    throw deleteError;
  }

  await reindexRemainingComponents(
    user_id,
    data,
    from ?? "landing-page",
    reindexMode
  );

  if (typeof refetch === 'function') {
    refetch();
  }
  if (String(type ?? "").trim() === "campaign") {
    notifyCampaignsUpdated(user_id);
  }

  maybeMarkLinkInBioContentEdited(from, user_id, username);

  // refetch();
  return deletedLinkedCreatorPageId;
}

export async function deleteEmbedComponent(
  name: any,
  user_id: any,
  data: any,
  dataToDelete: any,
  refetch: any,
  username: any,
  reindexMode: "live" | "archive" = "live"
) {
  return deleteComponent(
    name,
    user_id,
    data,
    dataToDelete,
    refetch,
    username,
    "embed-search",
    null,
    reindexMode
  );
}

export async function updateShopLink(
 unique_id: any,
 username: any,
 user_id: any,
 table: any
) {
  const {data, error} = await supabase
    .from(username)
    .select("*")
    .ilike("shop_link", `%/${unique_id}%`)
    .eq("user_id", user_id);
  if (data && data.length > 0) {
    for (const item of data) {
      const { data: updateData, error } = await supabase
        .from(username)
        .update({ shop_link: null })
        .eq("post_id", item.post_id)
        .eq("user_id", user_id);
    }
  }
} 
export async function changeComponentStatus(
  value: any,
  user_id: any,
  component: any,
  from: any
) {
  if (shouldDeferWebsiteLibWrite(from, component?.id)) {
    applyWebsiteLibLocalPatch(component?.id, {
      status_expiry: value,
      status: value,
    });
    return;
  }
  const tableName = getComponentsTableName(from);
  const archivePatch = await buildArchiveStatusPatch(value, user_id, from);
  const updatePayload: Record<string, any> = {
    status_expiry: value,
    status: value,
    ...archivePatch,
  };
  const manualStatuses = new Set(["active", "draft", "archive"]);
  if (manualStatuses.has(String(value || "").toLowerCase().trim())) {
    updatePayload.start_date = null;
    updatePayload.archive_date = null;
  }

  let query = supabase
    .from(tableName)
    .update(updatePayload)
    .eq("user_id", user_id)
    .eq("id", component.id);

  if (from !== "embed-search") {
    query = query.neq("status", "deleted");
  }

  if (isCreatorPagesMode(from) && component?.page_id) {
    query = query.eq("page_id", component.page_id);
  }

  let { data, error } = await query;

  if (error && isMissingArchiveColumnError(error)) {
    const fallbackPayload = {
      status_expiry: value,
      status: value,
      start_date: updatePayload.start_date,
      archive_date: updatePayload.archive_date,
    };
    let fallbackQuery = supabase
      .from(tableName)
      .update(fallbackPayload)
      .eq("user_id", user_id)
      .eq("id", component.id);
    if (from !== "embed-search") {
      fallbackQuery = fallbackQuery.neq("status", "deleted");
    }
    if (isCreatorPagesMode(from) && component?.page_id) {
      fallbackQuery = fallbackQuery.eq("page_id", component.page_id);
    }
    ({ data } = await fallbackQuery);
    Object.assign(updatePayload, fallbackPayload);
  }

  await syncLinkedCreatorComponentSettingsAfterUpdate({
    userId: user_id,
    from,
    previousComponent: component,
    nextComponent: {
      ...component,
      ...updatePayload,
    },
  });

  if (String(component?.type ?? "").trim() === "campaign") {
    notifyCampaignsUpdated(user_id);
  }

  maybeMarkLinkInBioContentEdited(from, user_id, null);

  return data;
}

export async function updateRoute(
  component: any,
  route: any,
  from: any,
  user_id: any
) {
  // Always persist route_name to the DB — public URLs resolve against it.
  // Still patch local editor state when the LiB publish gate is active.
  if (shouldDeferWebsiteLibWrite(from, component?.id)) {
    applyWebsiteLibLocalPatch(component?.id, { route_name: route });
  }
  const table = getComponentsTableName(from);
  let duplicateQuery = supabase
    .from(table)
    .select("id,unique_id")
    .eq("user_id", user_id)
    .neq("status", "deleted")
    .neq("id", component.id)
    .or(`route_name.eq.${route},unique_id.eq.${route}`);

  if (isCreatorPagesMode(from) && component?.page_id) {
    duplicateQuery = duplicateQuery.eq("page_id", component.page_id);
  }

  const { data: duplicates, error: duplicateError } = await duplicateQuery;

  if (duplicateError) {
    return { success: false, error: duplicateError.message };
  }
  if (duplicates && duplicates.length > 0) {
    return {
      success: false,
      error: "This route name is already taken. Please choose another.",
    };
  }

  const { data, error } = await supabase
    .from(table)
    .update({ route_name: route })
    .eq("id", component.id)
    .neq("status", "deleted");

  if (error) {
    return { success: false, error: error.message };
  }

  const nextComponent = {
    ...component,
    route_name: route,
  };

  // Website-template components are not linked to creator pages.
  if (from !== "website-template") {
    if (isCreatorPagesMode(from)) {
      await syncSourceComponentMetadataFromPageComponent({
        userId: user_id,
        previousPageComponent: component,
        nextPageComponent: nextComponent,
      });
    } else {
      await syncCreatorPageMetadataFromSourceComponent({
        userId: user_id,
        previousComponent: component,
        nextComponent,
      });
    }
  }

  maybeMarkLinkInBioContentEdited(from, user_id, null);

  return { success: true, data };
}

export async function updateCustomOrderPostIds(
  component: any,
  postIds: any,
  user_id: any,
  from?: any
) {
  if (shouldDeferWebsiteLibWrite(from, component?.id)) {
    applyWebsiteLibLocalPatch(component?.id, { custom_order: postIds });
    return { data: [{ ...component, custom_order: postIds }], error: null };
  }
  let query = supabase
    .from(getComponentsTableName(from))
    .update({ custom_order: postIds })
    .eq("id", component.id)
    .eq("user_id", user_id)
    .neq("status", "deleted");

  if (isCreatorPagesMode(from) && component?.page_id) {
    query = query.eq("page_id", component.page_id);
  }

  const { data, error } = await query;

  if (!error) {
    await syncLinkedCreatorComponentSettingsAfterUpdate({
      userId: user_id,
      from,
      previousComponent: component,
      nextComponent: {
        ...component,
        custom_order: postIds,
      },
    });
    maybeMarkLinkInBioContentEdited(from, user_id, null);
  }

  return { data, error };
}

/**
 * Resolves a unique component name with targeted DB lookups.
 * Common case: one indexed `.eq("name")` check. Conflicts fetch only the
 * candidate batch via `.in("name", ...)`, never the full component list.
 * Reusable for create/rename/duplicate of any component type.
 */
export async function allocateUniqueComponentName({
  tableName,
  userId,
  desiredName,
  pageId,
}: {
  tableName: string;
  userId: string;
  desiredName: string;
  pageId?: string | null;
}) {
  const desired = trimComponentName(desiredName) || "Untitled";

  const first = await assertInsertAllowedOrClearHiddenByName(
    tableName,
    userId,
    desired,
    pageId
  );
  if (first.allowed) return desired;

  for (
    let batchStart = 2;
    batchStart <= UNIQUE_COMPONENT_NAME_MAX_SUFFIX;
    batchStart += UNIQUE_COMPONENT_NAME_BATCH_SIZE
  ) {
    const remaining = UNIQUE_COMPONENT_NAME_MAX_SUFFIX - batchStart + 1;
    const candidates = buildUniqueNameCandidates(
      desired,
      batchStart,
      Math.min(UNIQUE_COMPONENT_NAME_BATCH_SIZE, remaining)
    );
    if (candidates.length === 0) break;

    let query = supabase
      .from(tableName)
      .select("name,status,status_expiry")
      .eq("user_id", userId)
      .in("name", candidates)
      .neq("status", "deleted");

    if (pageId) {
      query = query.eq("page_id", pageId);
    }

    const { data, error } = await query;
    if (error) throw error;

    const visibleTakenNames = new Set(
      (Array.isArray(data) ? data : [])
        .filter((row) => getComponentStatus(row) !== "hidden")
        .map((row) => row?.name)
        .filter(Boolean)
    );

    for (const candidate of candidates) {
      if (visibleTakenNames.has(candidate)) continue;
      const { allowed } = await assertInsertAllowedOrClearHiddenByName(
        tableName,
        userId,
        candidate,
        pageId
      );
      if (allowed) return candidate;
    }
  }

  return `${desired} ${createComponentSlug()}`;
}

export type DuplicateComponentOptions = {
  /** Defaults to `" (copy)"`. */
  nameSuffix?: string;
};

/**
 * Clones any stay-up-to-date / page / embed component row:
 * copies settings + membership, forces Draft, and places the copy
 * immediately below the source. Type gating belongs at the call site.
 */
export async function duplicateComponent(
  component: any,
  userId: string,
  from?: string,
  options?: DuplicateComponentOptions
) {
  const componentId = getComponentRecordId(component);
  if (!componentId || !userId) {
    throw new Error("Unable to duplicate component");
  }

  const tableName = getComponentsTableName(from);
  const scopedPageId = isCreatorPagesMode(from)
    ? getCreatorPagesScope(undefined, component)
    : null;

  if (isCreatorPagesMode(from) && !scopedPageId) {
    throw new Error("No active creator page selected");
  }

  const source = component;
  const includeRouteSlug =
    isCreatorPagesMode(from) || from === "embed-search";
  const copyName = await allocateUniqueComponentName({
    tableName,
    userId,
    desiredName: buildComponentCopyName(source.name, options?.nameSuffix),
    pageId: scopedPageId,
  });
  const sourceIsArchived = getComponentStatus(source) === "archive";

  let sourceIndex = 0;
  if (sourceIsArchived) {
    // Copies from archive are Drafts at the top of the live list (highest index).
    sourceIndex = await getNextLiveIndex(tableName, userId, scopedPageId);
  } else {
    let indexQuery = supabase
      .from(tableName)
      .select("index")
      .eq("id", componentId)
      .eq("user_id", userId);
    if (scopedPageId) indexQuery = indexQuery.eq("page_id", scopedPageId);
    const { data: indexRow, error: indexError } = await indexQuery.maybeSingle();
    if (indexError) throw indexError;
    const sourceIndexRaw = Number(indexRow?.index);
    sourceIndex = Number.isFinite(sourceIndexRaw) ? sourceIndexRaw : 0;
  }

  const payload = buildDuplicatedComponentPayload(source, {
    copyName,
    userId,
    index: sourceIndex,
    uniqueSlug: includeRouteSlug ? createComponentSlug(copyName) : undefined,
    pageId: scopedPageId,
    includeRouteSlug,
  });

  const { data: inserted, error: insertError } = await supabase
    .from(tableName)
    .insert([payload])
    .select("*")
    .single();

  if (insertError) throw insertError;
  if (!inserted) {
    throw new Error("Failed to duplicate component");
  }

  if (!sourceIsArchived) {
    await placeLiveComponentBelowSource({
      tableName,
      userId,
      pageId: scopedPageId,
      sourceIndex,
      excludeId: String(inserted.id),
    });
  }

  return {
    ...inserted,
    status: "draft",
    status_expiry: "draft",
    index: sourceIndex,
  };
}

export async function duplicateCustomShoppableCarousel(
  component: any,
  userId: string,
  from?: string,
  options?: DuplicateComponentOptions
) {
  if (!isCustomShoppableCarousel(component)) {
    throw new Error("Duplicate is only available for custom shoppable carousels");
  }
  return duplicateComponent(component, userId, from, options);
}

type ReplaceHiddenCarouselArgs = {
  userId: string;
  from?: string;
  newComponentName: string;
  displayName?: string;
  type?: string;
  shoppableContentFilter?: string;
  /** When hidden row was already soft-deleted before create. */
  hiddenCarouselId?: string | null;
};

/**
 * Replaces bootstrapped hidden carousel references with a newly created carousel.
 * This is best-effort and intentionally non-throwing for no-op scenarios.
 */
export async function replaceHiddenCarouselAfterCreate({
  userId,
  from,
  newComponentName,
  displayName,
  type,
  shoppableContentFilter,
  hiddenCarouselId,
}: ReplaceHiddenCarouselArgs) {
  const tableName = getComponentsTableName(from);

  const { data: created } = await supabase
    .from(tableName)
    .select("id,name")
    .eq("user_id", userId)
    .eq("name", newComponentName)
    .neq("status", "deleted")
    .order("index", { ascending: false })
    .limit(1)
    .maybeSingle();

  const newId = (created as any)?.id;
  if (!newId) return;

  let hiddenId = hiddenCarouselId ? String(hiddenCarouselId) : "";

  if (!hiddenId) {
    let hiddenQuery = supabase
      .from(tableName)
      .select("id,name,post_ids,status,status_expiry")
      .eq("user_id", userId)
      .neq("status", "deleted")
      .neq("name", newComponentName)
      .order("index", { ascending: false })
      .limit(5);

    if (displayName) hiddenQuery = hiddenQuery.eq("display_name", displayName);
    if (type) hiddenQuery = hiddenQuery.eq("type", type);
    if (shoppableContentFilter) {
      hiddenQuery = hiddenQuery.eq("shoppable_content_filter", shoppableContentFilter);
    }

    const { data: hiddenMatches } = await hiddenQuery;
    const hidden = (Array.isArray(hiddenMatches) ? hiddenMatches : []).find(
      (row) => getComponentStatus(row) === "hidden"
    );
    hiddenId = String((hidden as { id?: string | number })?.id ?? "");
  }

  if (!hiddenId || hiddenId === String(newId)) return;

  await swapCarouselIdInCollections(tableName, userId, hiddenId, String(newId));

  const { data: hiddenStillExists } = await supabase
    .from(tableName)
    .select("id,status,status_expiry")
    .eq("id", hiddenId)
    .eq("user_id", userId)
    .maybeSingle();

  if (
    hiddenStillExists &&
    getComponentStatus(hiddenStillExists) === "hidden"
  ) {
    await softDeleteComponentRow(tableName, userId, hiddenId);
  }
}
